import styles from './Items.module.css';

import { useState, useMemo, useEffect } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

import { useAuth } from "../../AuthContext";

import { Filter } from '../Filter/Filter';
import { ItemsHeader } from './ItemsHeader';
import { ItemsFooter } from './ItemsFooter';
import { ItemsList } from './ItemsList';
import { ItemsCart } from './ItemsCart';
import { Checkout } from '../Checkout/Checkout';

const ITEMS_PER_PAGE = 20;

export const Items = ({ user, myList, items, isPatroller, onNewItem, onSaveItem, onCopyItems, onRemoveItems, onVerifyItems, onSellerPayout, onCancel, size = ITEMS_PER_PAGE, onPaymentSuccess, verifying = false, selectedItems: parentSelectedItems, onSelectItem, onSelectAll }) => {
    const [ error, setError ] = useState(null);
    const [ page, setPage ] = useState(1);
    const [ selectedItems, setSelectedItems ] = useState(parentSelectedItems || []);
    const [ cartOpen, setCartOpen ] = useState(false);
    const [ checkoutOpen, setCheckoutOpen ] = useState(false);
    const [ verifyDialogOpen, setVerifyDialogOpen ] = useState(false);
    const [ successMessage, setSuccessMessage ] = useState(null);
    const [ payoutLoading, setPayoutLoading ] = useState(false);
    const [ filters, setFilters ] = useState({
        search: '',
        showCashTags: false,
        showVerifiedItems: false,
        showSoldItems: false,
        showPaidOutItems: false,
        showDonatedItems: false,
        invertFilters: false
    });

    const { claims, token } = useAuth();

    const pageSize = Math.max(size, 20);

    const sortedItems = useMemo(() => {
        return items.filter(item => {
            let include = true;

            if (filters.search && filters.search.trim() !== '') {
                const searchLower = filters.search.toLowerCase();

                const matchesName = item.name?.toLowerCase().includes(searchLower);
                const matchesDescription = item.description?.toLowerCase().includes(searchLower);
                const matchesBrand = item.brand?.toLowerCase().includes(searchLower);
                const matchesVendor = item.vendorName?.toLowerCase().includes(searchLower);
                const matchesGender = item.gender?.toLowerCase().includes(searchLower);
                const matchesColor = item.color?.toLowerCase().includes(searchLower);
                const matchesCategory = item.category?.toLowerCase().includes(searchLower);
                const matchesCondition = item.condition?.toLowerCase().includes(searchLower);
                const matchesPrice = item.price?.toString().toLowerCase().includes(searchLower);
                const matchesQuantity = item.quantity?.toString().toLowerCase().includes(searchLower);

                if (filters.invertFilters) {
                    if (matchesName || matchesDescription || matchesBrand || matchesVendor || matchesGender || matchesColor || matchesCategory || matchesCondition || matchesPrice || matchesQuantity) {
                        return false;
                    }
                } else {
                    if (!(matchesName || matchesDescription || matchesBrand || matchesVendor || matchesGender || matchesColor || matchesCategory || matchesCondition || matchesPrice || matchesQuantity)) {
                        return false;
                    }
                }
            }

            if (filters.showVerifiedItems) {
                if (filters.invertFilters) {
                    if (item.verified) {
                        return false;
                    }
                } else {
                    if (!item.verified) {
                        return false;
                    }
                }
            }

            if (filters.showSoldItems) {
                if (filters.invertFilters) {
                    if (item.sold) {
                        return false;
                    }
                } else {
                    if (!item.sold) {
                        return false;
                    }
                }
            }

            if (filters.showPaidOutItems) {
                if (filters.invertFilters) {
                    if (item.payoutAmount) {
                        return false;
                    }
                } else {
                    if (!item.payoutAmount) {
                        return false;
                    }
                }
            }

            if (filters.showDonatedItems) {
                if (filters.invertFilters) {
                    if (item.donation) {
                        return false;
                    }
                } else {
                    if (!item.donation) {
                        return false;
                    }
                }
            }

            if (filters.showCashTags) {
                if (filters.invertFilters) {
                    if (!item.feePaid) {
                        return false;
                    }
                } else {
                    if (item.feePaid) {
                        return false;
                    }
                }
            }

            return include;
        });
    }, [ items, filters ]);

    // Sync with parent selectedItems when it changes
    useEffect(() => {
        if (parentSelectedItems) {
            setSelectedItems(parentSelectedItems);
        }
    }, [ parentSelectedItems ]);

    const currentPageData = useMemo(() => {
        const pageIndex = page - 1;

        return sortedItems.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
    }, [ sortedItems, page, pageSize ]);

    const paginationInfo = useMemo(() => {
        const totalPages = Math.ceil(sortedItems.length / pageSize);

        return { totalPages };
    }, [ sortedItems.length, pageSize ]);

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1); // Reset to first page when filters change
    };

    const handleClearFilters = () => {
        setFilters({ search: '', showPaidOutItems: false, showSoldItems: false, showVerifiedItems: false, showDonatedItems: false, showCashTags: false, invertFilters: false });
        setPage(1);
    };

    const handleCheckboxChange = (item, checked) => {
        const newSelectedItems = checked
            ? [ ...selectedItems, item ]
            : selectedItems.filter(i => i.id !== item.id);

        setSelectedItems(newSelectedItems);

        // Call parent callback if provided
        if (onSelectItem) {
            onSelectItem(newSelectedItems);
        }
    };

    const handleOpenCart = () => {
        setCartOpen(true);
    };

    const handleCloseCart = () => {
        setCartOpen(false);
    };

    const handleCheckout = () => {
        setCartOpen(false);
        setCheckoutOpen(true);
    };

    const handleCheckoutSuccess = (result) => {
        setCheckoutOpen(false);
        setSelectedItems([]);

        // Only mark items as paid for completed payments, not pending cash
        if (result.status !== 'PENDING_CASH') {
            onPaymentSuccess && onPaymentSuccess(selectedItems);
        }

        if (result.paymentMethod === 'cash') {
            setSuccessMessage(`${ result.cashInstructions || 'You can pay your listing fees with cash when dropping off your items for the ski swap. The items must be dropped off on Friday, Nov 7 between 5:00pm and 8:00pm or on Saturday, Nov 8 between 8:00am and 10:00am.' }`);
        } else {
            setSuccessMessage(`Listing fee payment successful!`);
        }

        // Clear success message after 3 seconds
        const timeout = 3000;
        setTimeout(() => setSuccessMessage(null), timeout);
    };

    const handleCheckoutError = (error) => {
        console.error('Payment failed:', error);

        setError(`Payment failed: ${ error.message || 'Unknown error' }`);
        // Error is handled within the Checkout component
    };

    const handleCheckoutCancel = () => {
        setCheckoutOpen(false);
    };

    const handleRemoveFromCart = (item) => {
        if (item.feePaid) {
            // Prevent removal of paid items
            return;
        }
        onRemoveItems([ item.id ]);
    };

    const handleRemoveItems = async () => {
        if (selectedItems.length > 0) {
            // Remove vendor items (regardless of feePaid) and unpaid user items
            const removableItems = selectedItems.filter(item => item.vendor || !item.feePaid);

            if (removableItems.length > 0) {
                onRemoveItems(removableItems.map(i => i.id));
            }

            // Keep paid user items (non-vendor) selected
            setSelectedItems(selectedItems.filter(item => !item.vendor && item.feePaid));
        }
    };

    const handleCopyItems = async () => {
        if (selectedItems.length > 0) {
            onCopyItems(selectedItems);

            setSelectedItems([]);
        }
    };

    const handleCancel = () => {
        setSelectedItems([]);

        onCancel();
    };

    const handleSelectAll = (e) => {
        const { checked } = e.target;

        const newSelectedItems = checked ? currentPageData : [];
        setSelectedItems(newSelectedItems);

        // Call parent callback if provided
        if (onSelectAll) {
            onSelectAll(e);
        } else if (onSelectItem) {
            onSelectItem(newSelectedItems);
        }
    };

    const handleVerifySelected = async () => {
        if (selectedItems.length === 0) return;

        // Check if any items need fee payment
        const unpaidItems = selectedItems.filter(item => !item.feePaid);

        if (unpaidItems.length > 0) {
            // Show dialog to confirm fee collection
            setVerifyDialogOpen(true);
        } else {
            // No fees to collect, proceed with verification
            await performVerification();
        }
    };

    const performVerification = async () => {
        setError(null);
        setVerifyDialogOpen(false);

        const result = await onVerifyItems();

        if (result?.error) {
            setError(result?.error);
        } else if (result?.results) {
            // Update local items state - but since items is prop, we need to notify parent
            // For now, assume parent handles it
            setSelectedItems([]);
        }
    };

    const handleCancelVerification = () => {
        setVerifyDialogOpen(false);
    };

    const handleSellerPayout = async () => {
        if (selectedItems.length === 0) return;

        setError(null);
        setPayoutLoading(true);

        const result = await onSellerPayout();

        if (result?.error) {
            setError(result?.error);
        } else if (result?.results) {
            // Update local items state - but since items is prop, we need to notify parent
            // For now, assume parent handles it
            setSelectedItems([]);

            setSuccessMessage(`Successfully paid out seller for ${ selectedItems.length } items: $${ result.payout.toFixed(2) }`);
        }

        setPayoutLoading(false);
    };

    if (error) {
        return <Alert variant="filled" severity="error" onClose={ () => setError(null) }>{ error }</Alert>;
    }

    if (successMessage) {
        return <Alert variant="filled" severity="success" onClose={ () => setSuccessMessage(null) }>{ successMessage }</Alert>;
    }

    // MARK: render
    return (
        <div
            className={ styles.itemContainer }
            style={ {
                borderColor: myList ? 'rgba(83, 204, 255, 0.6)' : 'rgba(255, 132, 0, 0.6)'
            } }>

            <ItemsHeader
                count={ sortedItems.length }
                cartCount={ selectedItems.filter(item => !item.feePaid).length }
                myList={ myList }
                isAdmin={ claims?.admin }
                verifying={ verifying }
                selectedItems={ selectedItems }
                onNewItem={ onNewItem }
                onOpenCart={ handleOpenCart }
                onVerifySelected={ handleVerifySelected }
            />

            <ItemsCart
                open={ cartOpen }
                isPatroller={ isPatroller }
                onClose={ handleCloseCart }
                items={ selectedItems }
                onCheckout={ handleCheckout }
                onRemoveItem={ handleRemoveFromCart }
            />

            <Dialog
                open={ checkoutOpen }
                onClose={ handleCheckoutCancel }
                maxWidth="lg"
                fullWidth
                disableBackdropClick={ true }
                disableEscapeKeyDown={ true }
            >
                <Checkout
                    items={ selectedItems }
                    isPatroller={ isPatroller }
                    onPaymentSuccess={ handleCheckoutSuccess }
                    onPaymentError={ handleCheckoutError }
                    onCancel={ handleCheckoutCancel }
                    token={ token }
                />
            </Dialog>

            <Dialog
                open={ verifyDialogOpen }
                onClose={ handleCancelVerification }
                maxWidth="sm"
                fullWidth
                scroll="paper"
            >
                <DialogTitle>Verify Items and Collect Fees</DialogTitle>

                <DialogContent dividers sx={{ maxHeight: '60vh' }}>
                    <DialogContentText>
                        { (() => {
                            const unpaidItems = selectedItems.filter(item => !item.feePaid);
                            const totalFees = unpaidItems.reduce((sum, item) => {
                                // Calculate listing fee (10% of price, min $2)
                                const listingFee = Math.max(2, item.price * 0.10);
                                return sum + listingFee;
                            }, 0);

                            if (unpaidItems.length > 0) {
                                return `Please collect $${ totalFees.toFixed(2) } in listing fees from the customer for ${ unpaidItems.length } item(s).`;
                            }
                            return 'All selected items have listing fees paid. Proceed with verification?';
                        })() }
                    </DialogContentText>

                    <Typography variant="h6" sx={ { mt: 2, mb: 1 } }>
                        Selected Items ({ selectedItems.length }):
                    </Typography>

                    <List dense sx={ { maxHeight: 300, overflow: 'auto' } }>

                        { selectedItems.map((item, index) => (
                            <ListItem key={ item.id || index }>
                                <ListItemText
                                    primary={ `${ item.name } ${ item.brand ? `- ${ item.brand }` : '' }` }
                                    secondary={
                                        <>
                                            Price: ${ item.price?.toFixed(2) || '0.00' }
                                            { !item.feePaid && ` • Fee: $${ Math.max(2, item.price * 0.10).toFixed(2) }` }
                                            { item.feePaid && ' • ✓ Fee Paid' }
                                        </>
                                    }
                                />
                            </ListItem>
                        )) }

                    </List>
                </DialogContent>

                <DialogActions>
                    <Button onClick={ handleCancelVerification }>Cancel</Button>

                    <Button
                        onClick={ performVerification }
                        variant="contained"
                        color="primary"
                    >
                        { (() => {
                            const unpaidItems = selectedItems.filter(item => !item.feePaid);
                            const totalFees = unpaidItems.reduce((sum, item) => {
                                const listingFee = Math.max(2, item.price * 0.10);
                                return sum + listingFee;
                            }, 0);
                            return unpaidItems.length > 0 ? `Confirm $${ totalFees.toFixed(2) }` : 'Confirm';
                        })() }
                    </Button>
                </DialogActions>
            </Dialog>

            <Filter
                filters={ filters }
                filterItems={ true }
                onFilterChange={ handleFilterChange }
                onClearFilters={ handleClearFilters }
            />

            <ItemsList
                user={ user }
                items={ currentPageData }
                selectedItems={ selectedItems }
                onCheckboxChange={ handleCheckboxChange }
                onSelectAll={ handleSelectAll }
                onSaveItem={ onSaveItem }
                onCancel={ handleCancel }
                onSellerPayout={ onSellerPayout }
            />

            { sortedItems.length > 0 && <ItemsFooter
                paginationInfo={ paginationInfo }
                myList={ myList }
                userIsAdmin={ claims?.admin }
                onCopyItems={ handleCopyItems }
                onRemoveItems={ handleRemoveItems }
                onVerifyItems={ handleVerifySelected }
                onSellerPayout={ handleSellerPayout }
                selectedItems={ selectedItems }
                disableCopyRemove={ selectedItems.length === 0 }
                onSetPage={ setPage }
                currentPage={ page }
                paying={ payoutLoading }
            /> }
        </div>
    );
};